import { gql } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { BrightnessIcon } from '~components/ui/icons/BrightnessIcon';
import { Slider } from '~components/ui/Slider';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigDisplayBrightnessSlider_ConfigFragment,
  useConfigUpdateBrightnessMutation,
} from './__generated__/ConfigDisplayBrightnessSlider.graphql';

interface Props {
  config: ConfigDisplayBrightnessSlider_ConfigFragment;
  isDisabled?: boolean;
  onBrightnessLoadingStateChange: (state: boolean) => void;
  configStatus?: ConfigurationStatusType;
}

export function ConfigDisplayBrightnessSlider({
  config,
  isDisabled = false,
  onBrightnessLoadingStateChange,
  configStatus,
}: Props) {
  const toast = useToast();
  const [brightnessLevel, setBrightnessLevel] = useState(0);
  const analytics = useAnalyticsReporter();
  const { t } = useTranslation();
  useEffect(() => {
    const newBrightnessLevel = config.brightness?.reported;

    if (!isNil(newBrightnessLevel)) {
      setBrightnessLevel(newBrightnessLevel);
    }
  }, [config.brightness?.reported]);

  const [updateBrightness, { loading }] = useConfigUpdateBrightnessMutation();
  useEffect(() => {
    onBrightnessLoadingStateChange(loading);
  }, [loading, onBrightnessLoadingStateChange]);

  const handleSliderChangeEnd = useCallback(
    async (value: number) => {
      try {
        await updateBrightness({
          variables: {
            input: {
              configurationId: config.id,
              brightness: value,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
        analytics.track('displaySettingUpdate', { group: 'playback', changeItem: 'brightness' });
      } catch (err) {
        toast({
          status: 'error',
          title: t('brightnessUpdateErr'),
          description: fromError(err, 'BulkUpdateBrightness', {
            BRIGHTNESS_OUT_OF_RANGE: t('brightnessUpdateErrDesc'),
          }),
        });
      }
    },
    [analytics, updateBrightness, config.id, toast, t],
  );

  return (
    <Slider
      value={brightnessLevel}
      onChange={setBrightnessLevel}
      onChangeEnd={handleSliderChangeEnd}
      leftIcon={BrightnessIcon}
      min={0}
      max={100}
      isDisabled={checkStateToDisableTab(configStatus) ? true : isDisabled}
    />
  );
}

ConfigDisplayBrightnessSlider.graphql = {
  fragment: {
    ConfigDisplayBrightnessSlider_config: gql`
      fragment ConfigDisplayBrightnessSlider_config on ConfigurationModel {
        id
        brightness {
          reported
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateBrightness: gql`
      mutation ConfigUpdateBrightness($input: ConfigurationUpdateBrightnessInput!) {
        configurationUpdateBrightness(input: $input) {
          ...ConfigDisplayBrightnessSlider_config
        }
      }
    `,
  },
};
